import {
  AssessmentDocument,
  ConsentForm,
  ELECTRONIC_CONSENT_AGREEMENTS,
  ElectronicConsent,
  isConsentForm,
  UserElectronicConsent,
  WithId,
} from '@hb/shared'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useDocument } from '../../../../../hooks/backend/useDocument'
import { useAuth } from '../../../../../store/auth'

interface ElectronicConsentContextValue {
  isOpen: boolean
  consentRequired: boolean
  consent: ElectronicConsent | null
  toBeSignedByMe: boolean
  onClose: (forceClose?: boolean) => void
  onOpen: () => void
  consentLoading: boolean
  consentLoadingError: string | null
  consentForm: WithId<ConsentForm> | null
}

const ElectronicConsentContext = createContext<ElectronicConsentContextValue>({
  isOpen: false,
  consentRequired: false,
  consent: null,
  toBeSignedByMe: false,
  onClose: () => {},
  onOpen: () => {},
  consentLoading: false,
  consentLoadingError: null,
  consentForm: null,
})

export const ElectronicConsentProvider = ({
  children,
  document,
  onCloseDocument,
}: PropsWithChildren<{
  document: WithId<ConsentForm> | WithId<AssessmentDocument>
  onCloseDocument: () => void
}>) => {
  const { authUser } = useAuth()

  const consentForm = useMemo(() => {
    if (isConsentForm(document)) {
      return document
    }
    return null
  }, [document])
  const toBeSignedByUserId = useMemo(() => {
    if (consentForm) {
      return consentForm.toBeSignedByUser
    }
    return null
  }, [consentForm])

  const toBeSignedByMe = useMemo(() => {
    if (isConsentForm(document)) {
      return document.toBeSignedByUser === authUser?.uid
    }
    return false
  }, [document, authUser])

  const [isOpen, setIsOpen] = useState(!!(consentForm && toBeSignedByMe && !consentForm.signedOn))
  const {
    data: consentAgreementData,
    loading: consentLoading,
    error: consentLoadingError,
  } = useDocument<UserElectronicConsent>(ELECTRONIC_CONSENT_AGREEMENTS, toBeSignedByUserId)

  const mostRecentConsent = useMemo(() => {
    if (!consentAgreementData) return null
    return Object.values(consentAgreementData?.submissions ?? {})
      .filter(t => typeof t !== 'string')
      .sort((a, b) => new Date(b.signedOn).getTime() - new Date(a.signedOn).getTime())[0]
  }, [consentAgreementData])

  const consentRequired = useMemo(() => {
    if (!authUser) return false
    if (isConsentForm(document)) {
      return !consentLoading && !mostRecentConsent && toBeSignedByMe
    }
    return false
  }, [document, authUser, toBeSignedByMe, mostRecentConsent, consentLoading])
  const onClose = useCallback(
    (forceClose = false) => {
      if (consentRequired && !forceClose) {
        onCloseDocument()
      } else {
        setIsOpen(false)
      }
    },
    [consentRequired, onCloseDocument],
  )

  useEffect(() => {
    if (consentRequired) {
      setIsOpen(true)
    }
  }, [consentRequired])

  const contextValue = useMemo<ElectronicConsentContextValue>(
    () => ({
      isOpen,
      consentRequired,
      consentForm,
      consent: mostRecentConsent,
      toBeSignedByMe,
      consentLoading,
      consentLoadingError,
      onClose,
      onOpen: () => setIsOpen(true),
    }),
    [
      isOpen,
      consentRequired,
      consentForm,
      mostRecentConsent,
      toBeSignedByMe,
      consentLoading,
      consentLoadingError,
      onClose,
    ],
  )

  return (
    <ElectronicConsentContext.Provider value={contextValue}>
      {children}
    </ElectronicConsentContext.Provider>
  )
}

export const useElectronicConsent = () => useContext(ElectronicConsentContext)
